import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToParentElement, restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Box} from '@dropbox/dig-foundations';
import {useEffect, useMemo, useState} from 'react';

import {SortableHandle} from './SortableHandle';
import {useHoverReveal} from './useHoverReveal';
import {ArchivedFold, InsetDivider, ScanRow, ScanWorkstream} from './WorkstreamScanList';

// A few pixels of movement before a press on the handle becomes a drag.
const DRAG_ACTIVATION_PX = 4;

const SortableCard = ({
  workstream,
  first,
  last,
  handlesShown,
  rowHovered,
  onLeaveUpdate,
}: {
  workstream: ScanWorkstream;
  first: boolean;
  last: boolean;
  handlesShown: boolean;
  rowHovered: boolean;
  onLeaveUpdate: (id: number) => void;
}) => {
  const sortable = useSortable({id: workstream.id});
  const {setNodeRef, transform, transition, isDragging} = sortable;
  const {regionProps, revealed} = useHoverReveal();
  // On the handle (or carrying the card) the pointer is off the header, but the
  // card it moves stays lit.
  const [handleHovered, setHandleHovered] = useState(false);
  const corner = (rounded: boolean) => (rounded ? 'Medium' : undefined);
  return (
    // Unclipped, so the handle can hang off the box's left edge; the card inside
    // clips its own corners (the box's, when it's first / last). Opaque, so the
    // card being dragged covers the ones it passes; its divider travels with it.
    <Box
      ref={setNodeRef}
      data-sortable-row={workstream.id}
      {...regionProps}
      position="relative"
      style={{
        transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
        transition,
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      <SortableHandle
        sortable={sortable}
        shown={handlesShown || revealed}
        active={rowHovered}
        onHoverChange={setHandleHovered}
      />
      <Box
        backgroundColor="Background Base"
        boxShadow={isDragging ? 'Floating' : undefined}
        borderTopLeftRadius={corner(first)}
        borderTopRightRadius={corner(first)}
        borderBottomLeftRadius={corner(last)}
        borderBottomRightRadius={corner(last)}
        style={{overflow: 'hidden'}}
      >
        {!first && <InsetDivider />}
        <ScanRow
          workstream={workstream}
          depth={0}
          onLeaveUpdate={onLeaveUpdate}
          highlighted={handleHovered || isDragging}
        />
      </Box>
    </Box>
  );
};

/**
 * /workstreams: the detail page's sub-workstreams in their saved
 * order, one box like SubworkstreamGroup, each card draggable by a handle
 * hung off the box's left edge (all shown while the pointer is level with the
 * section, the row it's level with emphasized: `pointerRowId`). A drop shows the new order at
 * once and hands it to `onReorder` (which saves); the list follows the server
 * again once the saved order comes back changed (a save, an Undo, someone
 * else's edit). Archived ones stay in their collapsed fold at the end, not
 * sortable.
 */
export const SortableSubworkstreamGroup = ({
  workstreams,
  archived,
  pointerRowId,
  onReorder,
  onLeaveUpdate,
}: {
  workstreams: ScanWorkstream[];
  archived: ScanWorkstream[];
  // The card the pointer is level with (see usePointerBand): null between or
  // beside cards, undefined once the pointer is off the section.
  pointerRowId: number | null | undefined;
  // The new order, and the one on screen before the drop (for Undo).
  onReorder: (ids: number[], previousIds: number[]) => void;
  onLeaveUpdate: (id: number) => void;
}) => {
  const savedKey = workstreams.map(({id}) => id).join(',');
  const [order, setOrder] = useState(() => workstreams.map(({id}) => id));
  useEffect(() => {
    setOrder(workstreams.map(({id}) => id));
    // Only when the saved order itself changes, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);
  const byId = useMemo(() => new Map(workstreams.map((w) => [w.id, w])), [workstreams]);
  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_PX}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(order, order.indexOf(Number(active.id)), order.indexOf(Number(over.id)));
    setOrder(next);
    onReorder(next, order);
  };

  const cards = order.flatMap((id) => {
    const workstream = byId.get(id);
    return workstream ? [workstream] : [];
  });
  const hasFold = archived.length > 0;

  return (
    <Box borderStyle="Solid" borderColor="Border Subtle" borderWidth="1" borderRadius="Medium">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          {cards.map((workstream, index) => (
            <SortableCard
              key={workstream.id}
              workstream={workstream}
              first={index === 0}
              last={index === cards.length - 1 && !hasFold}
              handlesShown={pointerRowId !== undefined}
              rowHovered={pointerRowId === workstream.id}
              onLeaveUpdate={onLeaveUpdate}
            />
          ))}
        </SortableContext>
      </DndContext>
      {hasFold && (
        <Box
          borderTopLeftRadius={cards.length === 0 ? 'Medium' : undefined}
          borderTopRightRadius={cards.length === 0 ? 'Medium' : undefined}
          borderBottomLeftRadius="Medium"
          borderBottomRightRadius="Medium"
          style={{overflow: 'hidden'}}
        >
          {cards.length > 0 && <InsetDivider />}
          <ArchivedFold archived={archived} depth={0} onLeaveUpdate={onLeaveUpdate} />
        </Box>
      )}
    </Box>
  );
};
